/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useEffect, useMemo, useState } from "react";
import { BEM } from "@adapptio/lib-components";
import { ResourceService } from "../../../resources/ResourceService";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import { ITableColumn, ITableSortConfig, Table, TABLE_SORT, TableCol, TableRow, useTable } from "../Table";
import { TableFilter } from "../Table/TableFilter";
import { useFieldState } from "../../hooks";
import { FieldText } from "../Fields/FieldText";
import { sortTableData } from "../Table/TableUtil";
import { Icon, ICON_SIZE } from "../Icon";

import "./RouteList.scss";

const routeListDefaultSort: ITableSortConfig[] = [
	{
		columnId: "path",
		direction: TABLE_SORT.ASC
	}
];

const defaultRouteListColumns: ITableColumn[] = [
	{
		id: "label",
		// @todo Translate
		label: "Label",
		sortable: true,
		headerAlign: "left",
		width: "30%"
	},
	{
		id: "path",
		// @todo Translate
		label: "Path",
		sortable: true,
		headerAlign: "left"
	}
];

/**
 * Component props
 */
export interface IRouteListProps {
	resourceService: ResourceService;
	selectedRouteKey?: string;
	onSelect: (routeKey: string) => void;
	showCreateBtn?: boolean;
	onCreate?: () => void;
}

/**
 * Resource class list
 *
 * @param params
 */
export const RouteList: React.FunctionComponent<IRouteListProps> = ({
	resourceService,
	selectedRouteKey,
	onSelect,
	showCreateBtn,
	onCreate
}) => {
	const baseClass = BEM("edt-route-list");

	const [ items, setItems ] = useState(() => resourceService.getRouteList().slice());

	const routeListColumns = useMemo<ITableColumn[]>(() => {
		const cols: ITableColumn[] = [ ...defaultRouteListColumns ];

		if (showCreateBtn) {
			cols.push({
				id: "action",
				label: (
					<Icon
						name="mdi/plus"
						className={baseClass.element("clickable-icon").toClassName()}
						size={ICON_SIZE.XSMALL}
						onClick={onCreate}
					/>
				),
				sortable: false,
				headerAlign: "right"
			});
		}

		return cols;
	}, [ showCreateBtn, onCreate ]);

	const [ tableProps ] = useTable({
		columns: routeListColumns,
		defaultState: {
			selectionIds: [ selectedRouteKey ],
			sort: routeListDefaultSort
		}
	});

	const [ filter, setFilter ] = useFieldState({
		label: {
			defaultValue: ""
		},
		path: {
			defaultValue: ""
		}
	});

	const filteredAndSortedItems = useMemo(() => {
		return sortTableData(
			items.filter((item) => {
				if (
					filter.label.value &&
					!item.label.toLowerCase().includes(filter.label.value.toLowerCase())
				) {
					return false;
				}

				if (filter.path.value && !item.path.toLowerCase().includes(filter.path.value.toLowerCase())) {
					return false;
				}

				return true;
			}),
			tableProps.state.sort
		);
	}, [ items, filter.label.value, filter.path.value, tableProps.state.sort ]);

	useEffect(() => {
		const onRouteListChange = () => setItems(resourceService.getRouteList().slice());

		onEvent(resourceService.onRouteListChange, onRouteListChange);

		return () => {
			offEvent(resourceService.onRouteListChange, onRouteListChange);
		};
	}, [ resourceService ]);

	useEffect(() => {
		if (!tableProps.state.selectionIds.includes(selectedRouteKey)) {
			tableProps.onRowClick(selectedRouteKey);
		}
	}, [ selectedRouteKey ]);

	return (
		<div className={baseClass.toClassName()}>
			<div className={baseClass.element("container").toClassName()}>
				<Table {...tableProps} rowSelectable={true} onRowClick={onSelect}>
					<TableFilter>
						<TableCol>
							<FieldText
								value={filter.label.value}
								validationErrors={filter.label.validationErrors}
								onChange={setFilter.label}
								/* @todo Translate */
								placeholder="Search label..."
								grow={true}
							/>
						</TableCol>
						<TableCol colSpan={showCreateBtn ? 2 : undefined}>
							<FieldText
								value={filter.path.value}
								validationErrors={filter.path.validationErrors}
								onChange={setFilter.path}
								/* @todo Translate */
								placeholder="Search path..."
								grow={true}
							/>
						</TableCol>
					</TableFilter>
					{filteredAndSortedItems.map((item) => (
						<TableRow id={item.key} key={item.key} selectable>
							<TableCol>
								<span>{item.label}</span>
							</TableCol>
							<TableCol colSpan={showCreateBtn ? 2 : undefined}>
								<span>{item.path}</span>
							</TableCol>
						</TableRow>
					))}
				</Table>
			</div>
		</div>
	);
};
